import type React from 'react';

import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import DEFAULTVALUE from '@/app/constants/defaultValue';
import { EquipmentItem } from '@/app/models/detailsEquipment';
import { useUpdateDetailsEquipmentMutation } from '@/app/services/detailsEquipmentApi';
import {
  useSearchEquipmentAppropriationQuery,
  useSearchEquipmentClassificationQuery,
} from '@/app/services/financialClassificationApi';
import { formatDateForBackend } from '@/app/utils/dateUtils';
import formatterMonetaryValue, {
  cpfOrCpnjFromBack,
  formatCurrency,
  formatHandleCPFAndCNPJ,
  isSupplierCodeValid,
} from '@/app/utils/numberUtils';
import FormFieldDate from '@/components/common/DateField';
import DrawerButtons from '@/components/common/DrawerButtons';
import ModalPageTitle from '@/components/common/ModalPageTitle';
import useFormattedCpfAndCpnj from '@/hooks/useFormattedCpfAndCpnj';
import useFormattedCurrency from '@/hooks/useFormattedCurrency';
import { Box, useTheme } from '@mui/material';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import FormField from '../baseForms/FormField';
import FormFieldLong from '../baseForms/FormFieldLong';
import FormFieldQuantity from '../baseForms/FormFieldQuantity';
import FormFieldSelect from '../baseForms/FormFieldSelect';
import LaunchDetailsBox from '../baseForms/info/LaunchDetailsBox';

interface NewEditTeacherPaymentProps {
  selectItem: EquipmentItem | null;
  setDrawerOpen: (open: boolean) => void;
}

export default function NewEditEquipmentDetails(props: NewEditTeacherPaymentProps) {
  const theme = useTheme();
  const { t } = useTranslation(['equipment', 'common']);

  const { selectItem, setDrawerOpen } = props;

  const { value: totalValue, setValue: setTotalValue } = useFormattedCurrency(selectItem?.value);
  const { value: supplierCode, setValue: setSupplierCode } = useFormattedCpfAndCpnj(selectItem?.supplierCode);

  const [justification, setJustification] = useState<string>(selectItem?.justify ? selectItem.justify : '');
  const [launchComplement] = useState<string>(selectItem?.complement != null ? selectItem?.complement : '');
  const [classification, setClassification] = useState<string>(
    selectItem?.classification != null ? selectItem?.classification.id : '',
  );
  const [appropriation, setAppropriation] = useState<string>(
    selectItem?.appropriation != null ? selectItem?.appropriation.id : '',
  );
  const [description, setDescription] = useState<string>(
    selectItem?.description != null ? selectItem?.description : '',
  );
  const [quantity, setQuantity] = useState<number | null>(selectItem?.quantity || DEFAULTVALUE);

  const [supplier, setSupplier] = useState<string>(selectItem?.supplier != null ? selectItem?.supplier : '');
  const [invoiceCode, setInvoiceCode] = useState<string>(
    selectItem?.invoiceCode != null ? selectItem?.invoiceCode : '',
  );

  const acquisition = selectItem?.acquisition ? dayjs(selectItem.acquisition, 'YYYY-MM-DD') : null;

  const [expenseDate, setExpenseDate] = useState<string>(
    selectItem?.expenseDate ? dayjs(selectItem?.expenseDate).format('DD/MM/YYYY') : '',
  );

  const [update] = useUpdateDetailsEquipmentMutation();

  const handleSubmit = async () => {
    setDrawerOpen(false);
    await toast.promise(
      update({
        id: selectItem?.id as number,
        value: parseFloat(totalValue.replace('R$', '').replace('.', '').replace(',', '.')) || DEFAULTVALUE,
        description,
        quantity: quantity as number,
        supplier,
        supplierCode: cpfOrCpnjFromBack(supplierCode),
        invoiceCode,
        classification: {
          id: Number(classification),
        },
        appropriation: {
          id: Number(appropriation),
        },
        justify: justification,
        acquisition: acquisition?.format('YYYY-MM-DD') as string,
        expenseDate: formatDateForBackend(expenseDate as string),
      }).unwrap(),
      {
        pending: t('common:processing.wait.a.moment') as string,
        success: t('equipment.updated.successfully') as string,
        error: t('common:processing.failed') as string,
      },
      {
        position: toast.POSITION.TOP_RIGHT,
      },
    );
  };

  const handleCancel = () => {
    setDrawerOpen(false);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDescription(e.target.value);
  };

  const handleValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    const hasNegative = val.includes('-');
    const cleaned = val.replace(/[^0-9]/g, '');
    const raw = hasNegative ? `-${cleaned}` : cleaned;
    setTotalValue(formatCurrency(raw));
  };

  const { data: financialClassification } = useSearchEquipmentClassificationQuery();
  const { data: financialApropriation } = useSearchEquipmentAppropriationQuery();

  const handleSupplierCode = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatHandleCPFAndCNPJ(e.target.value);
    setSupplierCode(formatted);
  };
  const supplierCodeIsValid = isSupplierCodeValid(supplierCode);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 24px)',
        maxHeight: 'calc(100vh - 24px)',
        flexGrow: 'inherit',
      }}
    >
      <Box sx={{ flexGrow: 1 }}>
        <ModalPageTitle title={t('common:edit.detail')} />

        <LaunchDetailsBox
          totalValue={formatterMonetaryValue(selectItem?.value || null)}
          launchComplement={launchComplement}
          complementDisabled
        />

        <Box sx={{ width: '100%', height: theme.spacing(0.25), bgcolor: 'grey.300', mt: 2 }} />

        <Box sx={{ display: 'block', minWidth: theme.spacing(31), mb: 2 }}>
          <FormField label={t('common:description') as string} value={description} onChange={handleChange} />

          <FormFieldQuantity label={t('common:quantity')} value={quantity} onChange={setQuantity} />

          <FormField label={t('common:total.value') as string} value={totalValue} onChange={handleValueChange} />

          <FormField
            label={t('common:supplier') as string}
            value={supplier}
            onChange={(e) => {
              setSupplier(e.target.value);
            }}
          />

          <FormField
            label={t('common:cnpj') as string}
            value={supplierCode}
            onChange={handleSupplierCode}
            error={!supplierCodeIsValid}
          />

          <FormField
            label={t('common:invoice.number') as string}
            value={invoiceCode}
            onChange={(e) => {
              setInvoiceCode(e.target.value);
            }}
            error={!supplierCodeIsValid}
          />

          <FormFieldSelect
            label={t('classification.mct')}
            value={classification}
            onChange={setClassification}
            options={financialClassification ?? []}
            placeholder={t('select.classification') as string}
          />

          <FormFieldSelect
            label={t('appropriation.type')}
            value={appropriation}
            onChange={setAppropriation}
            options={financialApropriation ?? []}
            placeholder={t('select.apropriation') as string}
          />
          <FormFieldDate label={t('common:expenseDate')} value={expenseDate} onChange={setExpenseDate} />

          <FormFieldLong
            label={t('common:justification')}
            value={justification}
            onChange={(e) => setJustification(e.target.value)}
          />
        </Box>
      </Box>
      <Box sx={{ mt: 'auto' }}>
        <DrawerButtons
          onSave={handleSubmit}
          onCancel={handleCancel}
          disabled={!supplierCodeIsValid || !expenseDate || !classification || !appropriation}
        />
      </Box>
    </Box>
  );
}
